/**
 * SSD Intranet API Client
 * Connects React frontend to Apps Script JSON API backend
 * Handles transformation from raw API responses to normalized UI types
 */

import type {
  ApiEndpoint,
  DirectoryApiResponse,
  FacetsApiResponse,
  BrandingApiResponse,
  UserApiResponse,
  HealthApiResponse,
  DirectoryEntry,
  Facets,
  Branding,
  User,
  ApiDirectoryEntry,
  SmsOptInResponse,
  SmsOptInStatusResponse,
  UserPreferences,
} from '../types/api';

import type {
  BookmarksApiResponse,
  Bookmark,
} from '../types/bookmarks';

import {
  transformDirectoryEntry,
  transformBranding,
  transformUser,
} from '../types/api';

import {
  mockDirectoryEntries,
  mockFacets,
  mockBranding,
  mockUser,
  shouldUseMockData,
} from './mockData';

import { getAuthToken } from '../contexts/AuthContext';
import { getLocalAdminSettings, syncAdminSettingsWithServer } from '../utils/adminSync';
import { getDailyTasksList } from '../utils/dailyTasksSource';
import { ensureActiveSheetTabs } from '../utils/tabUtils';

// Re-export transform functions for external use
export { transformDirectoryEntry, transformBranding, transformUser };

// =============================================================================
// CONFIGURATION
// =============================================================================

const API_CONFIG = {
  // In development, use separate proxy server to avoid CORS
  // In production, use Cloudflare Pages Function proxy (handles CORS)
  baseUrl: import.meta.env.DEV
    ? 'http://localhost:3001'  // Local proxy server
    : '/api/proxy',           // Cloudflare Pages Function

  // Cache TTL in milliseconds (matches Apps Script 5-minute cache)
  cacheTtl: 5 * 60 * 1000,

  // Request timeout
  timeout: 30000,
};

// =============================================================================
// CACHE LAYER WITH LOCALSTORAGE PERSISTENCE
// =============================================================================

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const cache = new Map<string, CacheEntry<unknown>>();

function getCached<T>(key: string): T | null {
  const now = Date.now();

  // Check memory cache
  const memoryEntry = cache.get(key) as CacheEntry<T> | undefined;
  if (memoryEntry) {
    if (now - memoryEntry.timestamp < API_CONFIG.cacheTtl) {
      return memoryEntry.data;
    }
  }

  // Fallback to localStorage persistent cache
  try {
    const local = localStorage.getItem(`ssd_api_cache_${key}`);
    if (local) {
      const parsed = JSON.parse(local) as CacheEntry<T>;
      if (parsed && parsed.data && typeof parsed.timestamp === 'number') {
        if (now - parsed.timestamp < API_CONFIG.cacheTtl) {
          cache.set(key, parsed as CacheEntry<unknown>);
          return parsed.data;
        }
      }
      // Expired, remove stale entry from localStorage
      localStorage.removeItem(`ssd_api_cache_${key}`);
    }
  } catch {
    // Ignore localStorage read errors
  }

  return null;
}

function setCache<T>(key: string, data: T): void {
  const entry = { data, timestamp: Date.now() };
  cache.set(key, entry as CacheEntry<unknown>);
  try {
    localStorage.setItem(`ssd_api_cache_${key}`, JSON.stringify(entry));
  } catch {
    // Ignore localStorage write errors
  }
}

export function clearCache(): void {
  cache.clear();
  try {
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('ssd_api_cache_')) {
        localStorage.removeItem(key);
      }
    });
  } catch {
    // Ignore
  }
}

/**
 * Helper to merge persistent admin resource permission overrides from local persistence
 */
export function applyCustomPermissionOverrides(entries: DirectoryEntry[]): DirectoryEntry[] {
  try {
    const adminSettings = getLocalAdminSettings();
    const customSheetTitles = adminSettings.customSheetTitles || {};
    const overrides = adminSettings.resourcePermissions || {};

    return entries.map(entry => {
      const docKey = entry.description || entry.ssId || entry.name;
      const customTitle = customSheetTitles[docKey] || customSheetTitles[entry.description] || customSheetTitles[entry.name];
      const override = overrides[entry.id] || overrides[entry.name];

      let updated = entry;
      if (customTitle || (override && override.customDisplayName)) {
        updated = {
          ...updated,
          customDisplayName: customTitle || (override ? override.customDisplayName : undefined),
        };
      }

      if (override) {
        let overrideTeam = (override.team !== undefined ? override.team : updated.team) || '';
        const tLower = overrideTeam.toLowerCase().trim();
        if (tLower === 'unassigned' || tLower === 'no team' || tLower === 'management') {
          overrideTeam = '';
        }

        updated = {
          ...updated,
          team: overrideTeam,
          categories: override.categories !== undefined ? override.categories : updated.categories,
          accessEmails: override.accessEmails !== undefined ? override.accessEmails : updated.accessEmails,
        };
      }

      // Ensure all active sheets have Main Sheets category
      updated.categories = ['Main Sheets'];

      return updated;
    });
  } catch {
    return entries;
  }
}

// =============================================================================
// API FETCH HELPER
// =============================================================================

interface FetchResult<T> {
  success: boolean;
  data: T | null;
  error?: string;
  cached: boolean;
  cacheAge?: number;
}

async function fetchApi<T>(
  endpoint: ApiEndpoint,
  params: Record<string, string> = {},
  options: RequestInit = {}
): Promise<FetchResult<T>> {
  const cacheKey = `${endpoint}:${JSON.stringify(params)}`;

  // Check cache first
  const cached = getCached<T>(cacheKey);

  // Background fetch helper for Stale-While-Revalidate
  const doNetworkFetch = async (): Promise<FetchResult<T>> => {
    const url = new URL(API_CONFIG.baseUrl, window.location.origin);
    url.searchParams.set('api', 'true');
    url.searchParams.set('endpoint', endpoint);

    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        url.searchParams.set(key, value);
      }
    });

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);

      const headers: HeadersInit = {};
      const authToken = getAuthToken();
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      console.log(`[API Request] ${options.method || 'GET'} ${url.toString()}`);

      const response = await fetch(url.toString(), {
        method: options.method || 'GET',
        credentials: 'include',
        headers,
        body: options.body,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        console.error(`[API Error] HTTP ${response.status}`, response.statusText);
        if (response.status === 401) {
          window.dispatchEvent(new CustomEvent('auth-expired'));
        }
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json() as T;

      // Cache successful response
      setCache(cacheKey, data);

      return {
        success: true,
        data,
        cached: false,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      return {
        success: false,
        data: null,
        error: errorMessage,
        cached: false,
      };
    }
  };

  if (cached) {
    // Return cached data INSTANTLY (< 5ms) and revalidate in background
    doNetworkFetch().catch(() => {});

    const cacheEntry = cache.get(cacheKey);
    return {
      success: true,
      data: cached,
      cached: true,
      cacheAge: cacheEntry ? Date.now() - cacheEntry.timestamp : 0,
    };
  }

  // If no cache exists, perform network fetch
  return doNetworkFetch();
}

// =============================================================================
// API METHODS (return normalized types)
// =============================================================================

export interface DirectoryResult {
  success: boolean;
  entries: DirectoryEntry[];
  count: number;
  user?: string;
  error?: string;
  cached: boolean;
}

const DIRECTORY_STORAGE_CACHE_KEY = 'ssd_cached_directory_entries_v3';

/**
 * Synchronously load initial directory entries from local cache and master registries (0ms loading time)
 */
export function getInitialCachedDirectoryEntries(): DirectoryEntry[] {
  try {
    const cached = localStorage.getItem(DIRECTORY_STORAGE_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return ensureActiveSheetTabs(parsed);
      }
    }
  } catch {}

  const adminSettings = getLocalAdminSettings();
  const discovered = adminSettings.discoveredTabs || [];
  const combinedApiData: ApiDirectoryEntry[] = [];

  discovered.forEach((tab) => {
    combinedApiData.push({
      ssId: tab.ssId,
      tabName: tab.tabName,
      name: tab.name,
      url: tab.url,
      link: tab.url,
      categories: [],
      cats: [],
      desc: tab.desc,
      team: tab.team,
      access: tab.access,
    } as ApiDirectoryEntry);
  });

  let entries: DirectoryEntry[] = [];
  if (combinedApiData.length > 0) {
    const rawEntries = combinedApiData.map((apiEntry: ApiDirectoryEntry, index: number) =>
      transformDirectoryEntry(apiEntry, index)
    );
    entries = applyCustomPermissionOverrides(rawEntries);
  }

  const masterDailyEntries = getDailyTasksList();
  if (masterDailyEntries && masterDailyEntries.length > 0) {
    const seenUrls = new Set(entries.map((e) => (e.url || '').toLowerCase().trim().replace(/\/$/, '')));
    masterDailyEntries.forEach((dailyItem) => {
      const normUrl = (dailyItem.url || '').toLowerCase().trim().replace(/\/$/, '');
      if (normUrl && !seenUrls.has(normUrl)) {
        seenUrls.add(normUrl);
        entries.push(dailyItem);
      } else if (!normUrl) {
        entries.push(dailyItem);
      }
    });
  }

  return ensureActiveSheetTabs(entries);
}

/**
 * Get directory entries filtered by user access
 * Returns normalized DirectoryEntry[] for UI consumption
 */
export async function getDirectory(): Promise<DirectoryResult> {
  let rawApiEntries: ApiDirectoryEntry[] = [];
  let apiSuccess = false;
  let apiUser: string | undefined;

  try {
    const result = await fetchApi<DirectoryApiResponse>('directory', {
      all: 'true',
      admin: 'true',
      fullDirectory: 'true',
      bypassAccessFilter: 'true',
      global: 'true'
    });

    if (result.success && result.data && Array.isArray(result.data.data)) {
      apiSuccess = true;
      apiUser = result.data.user;
      // Filter out stale spreadsheet entries from the legacy Apps Script API
      rawApiEntries = result.data.data.filter((d: ApiDirectoryEntry) => {
        const isSpreadsheet = d.url && d.url.includes('docs.google.com/spreadsheets');
        return !isSpreadsheet;
      });
    }
  } catch (err) {
    console.warn('[API] Apps Script directory fetch failed, falling back to KV settings:', err);
  }

  const adminSettings = getLocalAdminSettings();
  const discovered = adminSettings.discoveredTabs || [];

  // Combine discovered tabs from KV with any external non-spreadsheet API entries
  const combinedApiData: ApiDirectoryEntry[] = [...rawApiEntries];
  discovered.forEach(tab => {
    combinedApiData.push({
      ssId: tab.ssId,
      tabName: tab.tabName,
      name: tab.name,
      url: tab.url,
      link: tab.url,
      categories: [],
      cats: [],
      desc: tab.desc,
      team: tab.team,
      access: tab.access
    } as ApiDirectoryEntry);
  });

  // Transform raw API entries to normalized entries
  let entries: DirectoryEntry[] = [];
  if (combinedApiData.length > 0) {
    const rawEntries = combinedApiData.map((apiEntry: ApiDirectoryEntry, index: number) =>
      transformDirectoryEntry(apiEntry, index)
    );
    entries = applyCustomPermissionOverrides(rawEntries);
  }

  // Merge with Master Daily Tasks entries extracted from Google Sheet (strictly deduplicated)
  const masterDailyEntries = getDailyTasksList();
  if (masterDailyEntries && masterDailyEntries.length > 0) {
    const seenUrls = new Set(entries.map(e => (e.url || '').toLowerCase().trim().replace(/\/$/, '')));
    masterDailyEntries.forEach(dailyItem => {
      const normUrl = (dailyItem.url || '').toLowerCase().trim().replace(/\/$/, '');
      if (normUrl && !seenUrls.has(normUrl)) {
        seenUrls.add(normUrl);
        entries.push(dailyItem);
      } else if (!normUrl) {
        entries.push(dailyItem);
      }
    });
  }

  // Persist updated directory cache to localStorage for instant subsequent loads
  if (entries.length > 0) {
    try {
      localStorage.setItem(DIRECTORY_STORAGE_CACHE_KEY, JSON.stringify(entries));
    } catch {}
  }

  const hasEntries = entries.length > 0;

  return {
    success: apiSuccess || hasEntries,
    entries,
    count: entries.length,
    user: apiUser,
    cached: false,
  };
}

export interface FacetsResult {
  success: boolean;
  facets: Facets | null;
  error?: string;
  cached: boolean;
}

/**
 * Get available filter facets (teams, categories)
 * Returns normalized Facets for UI consumption
 */
export async function getFacets(): Promise<FacetsResult> {
  try {
    const result = await fetchApi<FacetsApiResponse>('facets');
    if (result.success && result.data) {
      return {
        success: true,
        facets: {
          total: result.data.total || 0,
          teams: result.data.teams || [],
          categories: result.data.categories || [],
        },
        cached: result.cached,
      };
    }
  } catch (err) {
    console.warn('[API] Apps Script facets fetch failed:', err);
  }

  const adminSettings = getLocalAdminSettings();
  const deletedTeams = adminSettings.deletedTeams || [];
  const deletedSet = new Set(deletedTeams.map(t => t.toLowerCase().trim()));
  const teams = Array.from(new Set([
    ...(adminSettings.customTeams || []),
    ...(adminSettings.sheetCustomTeams || [])
  ]))
    .filter(t => t && t.trim() !== '' && t.toLowerCase() !== 'management' && t.toLowerCase() !== 'no team' && !deletedSet.has(t.toLowerCase().trim()))
    .map(name => ({ name, count: 0 }));

  return {
    success: true,
    facets: {
      total: (adminSettings.discoveredTabs || []).length,
      teams,
      categories: [{ name: 'Main Sheets', count: (adminSettings.discoveredTabs || []).length }],
    },
    cached: true,
  };
}

export interface BrandingResult {
  success: boolean;
  branding: Branding | null;
  error?: string;
  cached: boolean;
}

/**
 * Get branding configuration
 * Returns normalized Branding for UI consumption
 */
export async function getBranding(): Promise<BrandingResult> {
  try {
    const result = await fetchApi<BrandingApiResponse>('branding');
    if (result.success && result.data && result.data.branding) {
      return {
        success: true,
        branding: transformBranding(result.data.branding),
        cached: result.cached,
      };
    }
  } catch (err) {
    console.warn('[API] Apps Script branding fetch failed:', err);
  }

  return {
    success: true,
    branding: {
      title: 'Southern Skies Dermatology & Surgery Intranet',
      logoUrl: '/ssd-logo.png',
    },
    cached: true,
  };
}

export interface UserResult {
  success: boolean;
  user: User | null;
  error?: string;
  cached: boolean;
}

/**
 * Get current authenticated user
 * Returns normalized User for UI consumption
 */
export async function getCurrentUser(): Promise<UserResult> {
  try {
    const result = await fetchApi<UserApiResponse>('user');
    if (result.success && result.data && result.data.user) {
      return {
        success: true,
        user: transformUser(result.data.user, result.data.authenticated),
        cached: result.cached,
      };
    }
  } catch (err) {
    console.warn('[API] Apps Script user fetch failed:', err);
  }

  try {
    const storedAuth = localStorage.getItem('ssd_intranet_auth');
    if (storedAuth) {
      const authData = JSON.parse(storedAuth);
      if (authData.email) {
        return {
          success: true,
          user: {
            email: authData.email,
            isAuthenticated: true,
          },
          cached: true,
        };
      }
    }
  } catch {}

  return {
    success: false,
    user: null,
    cached: false,
  };
}

export interface HealthResult {
  success: boolean;
  status: 'healthy' | 'degraded' | 'unhealthy' | null;
  cacheStatus?: boolean;
  spreadsheetConnected?: boolean;
  error?: string;
  cached: boolean;
}

/**
 * Check API health status
 */
export async function getHealth(): Promise<HealthResult> {
  const result = await fetchApi<HealthApiResponse>('health');

  if (!result.success || !result.data) {
    return {
      success: false,
      status: null,
      error: result.error,
      cached: result.cached,
    };
  }

  const { data } = result;

  return {
    success: data.success,
    status: data.status,
    cacheStatus: data.cacheStatus,
    spreadsheetConnected: data.spreadsheetConnected,
    cached: result.cached,
  };
}

// =============================================================================
// SMS OPT-IN API METHODS
// =============================================================================

export interface SmsOptInResult {
  success: boolean;
  message: string;
  error?: string;
}

/**
 * Submit SMS opt-in request
 * @param phoneNumber - Phone number to receive SMS notifications
 * @param email - User's email address
 * @param isOptedIn - Whether user is opting in or out
 */
export async function submitSmsOptIn(
  phoneNumber: string,
  email: string,
  isOptedIn: boolean = true
): Promise<SmsOptInResult> {
  try {
    const url = new URL(API_CONFIG.baseUrl, window.location.origin);
    url.searchParams.set('api', 'true');
    url.searchParams.set('endpoint', 'sms-optin');

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    const authToken = getAuthToken();
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);

    const response = await fetch(url.toString(), {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify({
        phoneNumber,
        email,
        isOptedIn,
      }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json() as SmsOptInResponse;

    return {
      success: data.success,
      message: data.message || (data.success ? 'Successfully opted in!' : 'Failed to opt in'),
      error: data.error,
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return {
      success: false,
      message: 'Failed to submit opt-in request',
      error: errorMessage,
    };
  }
}

export interface SmsOptInStatusResult {
  success: boolean;
  isOptedIn: boolean;
  phoneNumber?: string;
  optedInAt?: string;
  error?: string;
}

/**
 * Get current SMS opt-in status for a user
 * @param email - User's email address
 */
export async function getSmsOptInStatus(email: string): Promise<SmsOptInStatusResult> {
  try {
    const url = new URL(API_CONFIG.baseUrl, window.location.origin);
    url.searchParams.set('api', 'true');
    url.searchParams.set('endpoint', 'sms-optin');
    url.searchParams.set('email', email);

    const headers: HeadersInit = {};
    const authToken = getAuthToken();
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);

    const response = await fetch(url.toString(), {
      method: 'GET',
      credentials: 'include',
      headers,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json() as SmsOptInStatusResponse;

    return {
      success: data.success,
      isOptedIn: data.isOptedIn || false,
      phoneNumber: data.phoneNumber,
      optedInAt: data.optedInAt,
      error: data.error,
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return {
      success: false,
      isOptedIn: false,
      error: errorMessage,
    };
  }
}


// =============================================================================
// GLOBAL ADMIN SETTINGS API METHODS
// =============================================================================

export interface GlobalAdminSettingsResult {
  success: boolean;
  data: any | null;
  error?: string;
}

/**
 * Save global admin settings to Cloudflare KV via edge function
 */
export async function saveGlobalAdminSettingsApi(settings: any): Promise<GlobalAdminSettingsResult> {
  try {
    const url = new URL('/api/admin-settings', window.location.origin);
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    const authToken = getAuthToken();
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    const response = await fetch(url.toString(), {
      method: 'POST',
      headers,
      body: JSON.stringify(settings)
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }

    const data = await response.json();
    return { success: data.success, data: data.data };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, data: null, error: errorMessage };
  }
}

/**
 * Fetch global admin settings from Cloudflare KV via edge function
 */
export async function getGlobalAdminSettingsApi(): Promise<GlobalAdminSettingsResult> {
  try {
    const url = new URL('/api/admin-settings', window.location.origin);
    
    const response = await fetch(url.toString(), {
      method: 'GET'
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }

    const data = await response.json();
    return { success: data.success, data: data.data };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, data: null, error: errorMessage };
  }
}

// =============================================================================
// BOOKMARKS API METHODS
// =============================================================================

export interface BookmarksResult {
  success: boolean;
  bookmarks: Bookmark[];
  error?: string;
  cached: boolean;
}

/**
 * Get bookmarks list
 */
export async function getBookmarks(): Promise<BookmarksResult> {
  // Mock data for development if needed
  if (shouldUseMockData() && import.meta.env.DEV) {
    return {
      success: true,
      bookmarks: [
        { name: 'ADP Payroll', url: 'https://adp.com', team: 'HR' },
        { name: 'Google Workspace', url: 'https://workspace.google.com', team: 'IT' },
        { name: 'Trello Board', url: 'https://trello.com', team: 'Marketing' },
        { name: 'Canva', url: 'https://canva.com', team: 'Marketing' },
      ],
      cached: false
    };
  }

  const result = await fetchApi<BookmarksApiResponse>('bookmarks');

  if (result.success && result.data && result.data.bookmarks && result.data.bookmarks.length > 0) {
    return {
      success: true,
      bookmarks: result.data.bookmarks,
      cached: result.cached,
    };
  }

  // Fallback: Map directory entries as bookmarks so Bookmarks page loads immediately
  const dirResult = await getDirectory();
  if (dirResult.success && dirResult.entries.length > 0) {
    const mapped: Bookmark[] = dirResult.entries.map(e => ({
      name: e.name,
      url: e.url,
      team: e.team,
      category: e.categories[0] || 'General',
    }));
    return {
      success: true,
      bookmarks: mapped,
      cached: dirResult.cached,
    };
  }

  return {
    success: false,
    bookmarks: [],
    error: result.error || 'Failed to load bookmarks',
    cached: false,
  };
}

// =============================================================================
// COMPOSITE LOADERS
// =============================================================================

export interface InitialData {
  directory: DirectoryResult;
  facets: FacetsResult;
  branding: BrandingResult;
  user: UserResult;
  success: boolean;
}

/**
 * Load all initial data for the app
 * Fetches directory, facets, branding, and user in parallel
 * Falls back to mock data in development when API auth is not available
 */
export async function loadInitialData(): Promise<InitialData> {
  // Use mock data if explicitly enabled or in dev mode without proxy
  if (shouldUseMockData()) {
    console.log('[API] Using mock data for development');
    return {
      directory: {
        success: true,
        entries: mockDirectoryEntries,
        count: mockDirectoryEntries.length,
        user: mockUser.email,
        cached: false,
      },
      facets: {
        success: true,
        facets: mockFacets,
        cached: false,
      },
      branding: {
        success: true,
        branding: mockBranding,
        cached: false,
      },
      user: {
        success: true,
        user: mockUser,
        cached: false,
      },
      success: true,
    };
  }

  // Ensure latest settings and discoveredTabs are loaded from KV
  await syncAdminSettingsWithServer().catch(() => {});

  const [directory, facets, branding, user] = await Promise.all([
    getDirectory(),
    getFacets(),
    getBranding(),
    getCurrentUser(),
  ]);

  // If API returns auth error and we're in dev, fall back to mock data
  const hasAuthError = !directory.success && directory.error?.includes('Authentication');
  if (import.meta.env.DEV && hasAuthError) {
    console.log('[API] Authentication required - falling back to mock data');
    return {
      directory: {
        success: true,
        entries: mockDirectoryEntries,
        count: mockDirectoryEntries.length,
        user: mockUser.email,
        cached: false,
      },
      facets: {
        success: true,
        facets: mockFacets,
        cached: false,
      },
      branding: {
        success: true,
        branding: mockBranding,
        cached: false,
      },
      user: {
        success: true,
        user: mockUser,
        cached: false,
      },
      success: true,
    };
  }

  const hasEntries = directory.entries && directory.entries.length > 0;

  return {
    directory,
    facets,
    branding,
    user,
    success: directory.success || hasEntries,
  };
}

// =============================================================================
// EXPORT CONFIG FOR TESTING
// =============================================================================

export interface PreferencesResult {
  success: boolean;
  preferences?: UserPreferences;
  error?: string;
}

export async function getUserPreferences(): Promise<PreferencesResult> {
  const result = await fetchApi<{ success: boolean; preferences: UserPreferences; error?: string }>('preferences', {
    global: 'true',
    all: 'true'
  });
  const backendError = result.data?.error;

  return {
    success: result.success && !backendError,
    preferences: result.data?.preferences,
    error: result.error || backendError
  };
}

export async function saveUserPreferences(preferences: UserPreferences): Promise<boolean> {
  const result = await fetchApi<{ success: boolean; message: string }>(
    'preferences',
    { global: 'true' },
    {
      method: 'POST',
      body: JSON.stringify({ preferences, global: true })
    }
  );
  return result.success;
}
export { API_CONFIG };
